import {
  Product as RTKProduct,
  ManufacturerCategory,
  OrderDetail as RTKOrderDetail,
  PurchaseOrder as RTKPurchaseOrder,
  Requisition as RTKRequisition,
  SalesStatistic as RTKSalesStatistic,
  OpportunityEntry as RTKOpportunityEntry,
  InventoryValueEntry as RTKInventoryValueEntry,
  Return as RTKReturn,
} from "randmar-api-client";
import { Product } from "../stores/product/Product";
import { Category } from "../stores/category/Category";
import {
  OrderDetail,
  PurchaseOrderPartNumber,
  RequisitionPartNumber,
} from "../stores/document/Document";
import { SalesStatistics } from "../stores/reseller/Reseller";
import { Opportunity } from "../stores/opportunities/Opportunity";
import { InventoryValue } from "../stores/manufacturer/Manufacturer";
import { Reseller as ApiReseller } from "randmar-api-client";
import LegacyReseller from "../stores/reseller/Reseller";
import Return from "../stores/return/Return";


export const toLegacyReseller = (reseller: ApiReseller): LegacyReseller => {
  const r = new LegacyReseller();
  Object.assign(r, {
    arEmail: reseller.AREmail,
    address1: reseller.Address1,
    address2: reseller.Address2,
    city: reseller.City,
    contactName: reseller.ContactName,
    createdDate: reseller.CreationDate,
    creditLimit: reseller.CreditLimit,
    defaultWarehouse: reseller.DefaultWarehouse,
    website: reseller.Website,
    isQualified: (reseller.Qualifications?.length ?? 0) > 0,
    resellerId: reseller.ResellerId,
    name: reseller.Name,
    phone: reseller.Phone,
    postalCode: reseller.PostalCode,
    province: reseller.Province,
    country: reseller.Country,
  });
  return r;
};

function convertCategoryToRTK(category: Category): ManufacturerCategory {
  return {
    CategoryName: category.categoryName,
    CategoryCode: category.categoryCode,
    Niche: category.niche,
    A_GP: category.a_gp,
    B_GP: category.b_gp,
    C_GP: category.c_gp,
    D_GP: category.d_gp,
    Cost_GP: category.cost_gp,
    LeadTime: parseInt(category.leadTime || "0"),
    MinimumOrder: parseInt(category.minimumOrder || "0"),
    WeeksOfStocking: parseInt(category.weeksOfStocking || "0"),
    InventoryValue: category.inventoryValue?.map(convertInventoryValueToRTK),
    Products: category.products?.map(convertLegacyProductToRTK),
    Starred: category.starred,
    AutoReorder: category.autoReorder,
    QualificationRequired: category.qualificationRequired,
  };
}

function convertOrderDetailToRTK(detail: OrderDetail): RTKOrderDetail {
  return {
    Category: detail.category,
    DocumentDate: detail.documentDate,
    OrderDate: detail.orderDate,
    DocumentNumber: detail.documentNumber,
    DocumentType: detail.documentType,
    ExtendedPrice: detail.extendedPrice,
    ExtendedWeight: detail.extendedWeight,
    MPN: detail.mpn,
    ManufacturerId: detail.manufacturerId,
    OpportunityManufacTurerId: detail.opportunityManufacTurerId,
    OpportunityManufacturerNumber: detail.opportunityManufacturerNumber,
    OrderNumber: detail.orderNumber,
    PONumber: detail.poNumber,
    ManufacturerName: detail.manufacturerName,
    Quantity: detail.quantity,
    RandmarSKU: detail.randmarSKU,
    ResellerId: detail.resellerId,
    ResellerName: detail.resellerName,
    ShipToName: detail.shipToName,
    ShipViaDescription: detail.shipViaDescription,
    UnitPrice: detail.unitPrice,
    UnitWeight: detail.unitWeight,
    WarehouseCode: detail.warehouseCode,
    ShipToZipCode: detail.shipToZipCode,
  };
}

function convertPurchaseOrderToRTK(
  po: PurchaseOrderPartNumber
): RTKPurchaseOrder {
  return {
    RandmarSKU: po.randmarSKU,
    Description: po.description,
    Location: po.location,
    QuantityOutstanding: po.quantity,
    MPN: po.mpn,
    UnitPrice: po.unitPrice,
    Category: po.category,
    PurchaseOrderNumber: po.purchaseOrderNumber,
    PurchaseOrderDate: po.purchaseOrderDate,
    LineNumber: po.lineNumber,
  };
}

function convertRequisitionToRTK(
  req: RequisitionPartNumber
): RTKRequisition {
  return {
    Location: req.location,
    QuantityOrdered: req.quantityOrdered,
    RequisitionNumber: req.requisitionNumber,
  };
}

function convertSalesStatisticsToRTK(
  stats: SalesStatistics
): RTKSalesStatistic {
  return {
    Day: stats.day,
    Quantity: stats.quantity,
    ExtendedPrice: stats.extendedPrice,
  };
}

function convertOpportunityToRTK(
  opp: Opportunity
): RTKOpportunityEntry {
  return {
    BidNumber: opp.bidNumber,
    Opportunity: opp.opportunity,
    Description: opp.description,
    StartDate: opp.startDate,
    EndDate: opp.endDate,
    LastUpdate: opp.lastUpdate,
    Active: opp.active,
    DefaultResellers: opp.defaultResellers,
    CanBeSetAsDefault: opp.canBeSetAsDefault,
    BidPrice: opp.bidPrice,
    BidPriceType: opp.bidPriceType,
    RandmarSKU: opp.randmarSKU,
  };
}

function convertInventoryValueToRTK(
  inv: InventoryValue
): RTKInventoryValueEntry {
  return {
    InventoryValue: inv.inventoryValue,
    PurchaseOrderValue: inv.purchaseOrderValue,
    SalesOrderValue: inv.salesOrderValue,
    Location: inv.location,
    Category: inv.category,
  };
}

export function convertLegacyReturnToRTK(
  legacyReturn: Return & {
    mpn?: string;
    quantity?: number;
    resellerName?: string;
    warehouseLocation?: string;
    comments?: string;
  }
): RTKReturn {
  return {
    ReturnNumber: legacyReturn.returnNumber,
    ResellerId:
      legacyReturn.resellerId != null
        ? String(legacyReturn.resellerId)
        : undefined,
    Status: legacyReturn.status,
    InvoiceNumber: legacyReturn.invoiceNumber,
    RequestDate: legacyReturn.requestDate,
    CreditNumber: legacyReturn.creditNumber,
    ContactName: legacyReturn.contactName,
    ContactPhone: legacyReturn.contactPhone,
    ContactEmail: legacyReturn.contactEmail,
    Comments: legacyReturn.comments,
    RandmarSKU: legacyReturn.mpn,
    Quantity: legacyReturn.quantity,
    ResellerName: legacyReturn.resellerName,
    WarehouseLocation: legacyReturn.warehouseLocation,
  };
}

export function convertRTKReturnToLegacy(apiReturn: RTKReturn): Return {
  const legacyReturn = new Return();
  Object.assign(legacyReturn, {
    returnNumber: apiReturn.ReturnNumber,
    resellerId: apiReturn.ResellerId ? parseInt(apiReturn.ResellerId, 10) : undefined,
    status: apiReturn.Status,
    invoiceNumber: apiReturn.InvoiceNumber,
    requestDate: apiReturn.RequestDate,
    creditNumber: apiReturn.CreditNumber,
    contactName: apiReturn.ContactName,
    contactPhone: apiReturn.ContactPhone,
    contactEmail: apiReturn.ContactEmail,
    comments: apiReturn.Comments,
    mpn: apiReturn.RandmarSKU,
    quantity: apiReturn.Quantity,
    resellerName: apiReturn.ResellerName,
    warehouseLocation: apiReturn.WarehouseLocation,
  });
  return legacyReturn;
}

export function convertLegacyProductToRTK(product: Product): RTKProduct {
  return {
    AutoUpdate: product.autoUpdate ? 1 : 0,
    Category: product.category,
    CreatedBy: product.createdBy,
    MPN: product.mpn,
    ManufacturerName: product.manufacturerName,
    MasterCarton: product.masterCarton,
    ProductType: product.productType,
    RandmarSKU: product.randmarSKU,
    SerialNumber: product.serialNumber,
    TransparencyCode: product.transparencyCode,
    OpportunityOnly: product.opportunityOnly,
    UPC: product.upc,
    UnitWeight: product.unitWeight,
    SkidQuantity: product.skidQuantity,
    IsManufacturer: product.isManufacturer,
    BodyHTML: product.bodyHTML,
    Title: product.title,
    MAP: product.map,
    MSRP: product.msrp,
    State: product.state,
    VoiceoverCaption: product.voiceoverCaption,
    SceneName: product.sceneName,
    CountryCodeOfOrigin: product.countryCodeOfOrigin,
    Require3DScan: product.require3DScan,
    Distribution: {
      Price: product.distribution?.price,
      RegularPrice: product.distribution?.regularPrice,
      Cost: product.distribution?.cost,
      Currency: product.distribution?.currency,
      Inventory: product.distribution?.Inventory,
    },
    InstantRebates: product.instantRebates,
    Requisitions: product.requisitionsPartNumbers?.map(convertRequisitionToRTK),
    PurchaseOrders: product.purchaseOrdersPartNumbers?.map(
      convertPurchaseOrderToRTK
    ),
    Opportunities: product.opportunities?.map(convertOpportunityToRTK),
    ManufacturerCategory: product.manufacturerCategory
      ? convertCategoryToRTK(product.manufacturerCategory)
      : undefined,
    ActiveOrderDetails: product.activeOrderDetails?.map(convertOrderDetailToRTK),
    CompletedOrderDetails: product.completedOrderDetails?.map(
      convertOrderDetailToRTK
    ),
    SalesStatistics: product.salesStatistics?.map(convertSalesStatisticsToRTK),
  };
}
